import { CurrencyPipe, LocationStrategy } from '@angular/common';
import { Component, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import { MatTable, MatTableDataSource } from '@angular/material/table';
import { MatPaginator } from '@angular/material/paginator';
import { IMenuOptions } from '../../models/IMenuOptions';
import { MatSort } from '@angular/material/sort';
import { SingleImageLoaderController } from '../../services/single-image-loader-controller/single-image-loader-controller.service';
import { FileUpload } from '../../models/IFileUpload';
import { IOption } from '../../models/IOption';
import { IColumnDetails, ITableData } from '../../models/ITableData';
import { ScrollService } from '../../services/scroll/scroll.service';
import { CSVService } from '../../services/csv-menu/csv.service';
import { IFileData } from '../../models/IFileData';
import { ITableExportData } from '../../models/ITableExportData';
import { StringHelpers } from '../../helpers/string-helpers';

@Component({
  selector: 'ff-input-table',
  templateUrl: './input-table.component.html',
  styleUrls: ['./input-table.component.scss']
})
export class InputTableComponent implements OnInit {

  constructor(private currencyPipe : CurrencyPipe, private singleImageLoaderController: SingleImageLoaderController, private scrollService: ScrollService) { }

  @ViewChild(MatTable) table: MatTable<ITableData>;
  @ViewChild(MatPaginator) paginator: MatPaginator;
  @ViewChild(MatSort) sort: MatSort;

  /**
   * The title name to show at the top of the table
   * @internal
   */
  @Input() titleName: string = 'Category';

  /**
   * The title to show at the top of the table
   * @internal
   */
  @Input() title: string = '';

  /**
   * The attributes belonging to each item in the table
   * @internal
   */
  @Input() inputProperties: IColumnDetails[] = [];

  /**
   * Custom function to validate the CSV file
   * @internal
   */
  @Input() validateCSV: (args: any) => { message: string, type: string }[] = () => [];

  /**
   * Return the options updated each time the user check a radio button
   * @internal
   */
  @Output() data = new EventEmitter<ITableData[]>();

  /**
   * Return the options updated each time the user check a radio button
   * @internal
   */
  @Output() csvErrors = new EventEmitter<{ message: string, type: string }[]>();

  /**
   * Options to display as menu items in the table
   * Use the IMenuOptions interface: { label: string; icon?: string; callback: () => void; args?: any[]; }
   * @internal
   */
  upperList: IMenuOptions[] = [
    { label: "Download sample CSV", icon: 'save_alt', callback: this.exportCSV },
    { label: "Import CSV", icon: 'import_export', callback: this.importCSV }
  ];

  //Base model with the abstract properties from the items in the table
  baseModel : ITableData = {};

  //Properties list
  modelList : ITableData[] = [];
  dataSource = new MatTableDataSource<ITableData>(this.modelList);
  multipleRows: boolean = false;
  tableRows: number = 1;
  imageElement : ITableData;
  missingInfo : boolean = false;

  //error counter
  private errorCounter: any[] = [];

    imageLoaderModal = async (event:Event) => {
      this.singleImageLoaderController.createSingleModal(
        "Add Photo",
        {
          onSave: this.onSave
        }
      );
      await this.singleImageLoaderController.present().then((value) => {
        this.updateModel();
      });
    }

  ngOnInit() {
    //adds item's properties to the base model
    this.inputProperties.forEach(prop => {
      this.baseModel[prop.columnName] = prop.baseModel;
    });

    //Creates new obj to create first row
    const obj = this.createRow();
    this.modelList.push(obj);

    //Updates table datasource and menu args
    this.dataSource.data = [...this.modelList];
    this.upperList[0].args = [{title: this.title, inputProperties: this.inputProperties, data: this.modelList}];
    this.upperList[1].args = this.modelList;
  }

  ngAfterViewInit() {
    this.dataSource.paginator = this.paginator;
    this.dataSource.sort = this.sort;

    setTimeout(() => {
      this.dataSource.paginator.pageSize = 5;
      this.dataSource.paginator.pageSizeOptions = [5, 10, 20, 100]
    });
  }

  ngDoCheck() {
    this.data.emit(this.modelList);
  }

  //Format data type currency
  transformAmount(element){
    element.value = this.currencyPipe.transform(
      element.value.replace(/[$,]/g, ''), 'USD','symbol','1.2-2');
  }

  //Converts input in case that it has '$' or ',' to be formatted and read by the model
  onFocusCost(event){
    let val = event.target.value as string;
    event.target.value = val.replace(/[$,]/g, '');
  }

  //Format data type months
  transformMonth(element){
    let value = element.value;

    if (!isNaN(value) && value !== ""){
      value = value + " mo";
    }
    element.value = value;
  }

  //Removes 'mo' format from the input at the moment of focusing on the input
  onFocusMonth(event){
    let val = event.target.value as string;
    val = val.slice(0, -3);
    event.target.value = val;
  }

  //Format data type days
  transformDays(element){
    let value = element.value;

    if (!isNaN(value) && value !== ""){
      value = value + " d";
    }
    element.value = value;
  }

  //Removes 'd' format from the input at the moment of focusing on the input
  onFocusDays(event){
    let val = event.target.value as string;

    val = val.slice(0, -2);

    event.target.value = val;
  }

  //function that copies first row in the model to the remaining rows in the list
  copyRows() {
    let cloneObj;
    let newArray = [];

    for (let i =0; i < this.modelList.length; i++){
      cloneObj = Object.assign({},this.modelList[0]);

      for (let prop in cloneObj){
        const property = this.inputProperties.find(x => x.columnName === prop); 

        if (property.cloneable){
          if (property.columnType === 'currency'){
            if(cloneObj[prop]){
              cloneObj[prop] = this.currencyPipe.transform(
                cloneObj[prop].replace(/[$,]/g, ''), 'USD','symbol','1.2-2');
            }
          }
          else if (property.columnType === 'days'){
            cloneObj[prop] = cloneObj[prop].slice(-2) === " d"
            ? cloneObj[prop]
            : cloneObj[prop] + ' d';
          }
          else if (property.columnType === 'months'){
            cloneObj[prop] = cloneObj[prop].slice(-3) === " mo"
            ? cloneObj[prop]
            : cloneObj[prop] + ' mo';
          }
        }
        else {
          cloneObj[prop] = this.modelList[i][prop];
        }
      }

      newArray.push(cloneObj);
    }

    this.modelList = newArray;
    this.updateModel();
  }

    //Format selected date
    dateSelection(element){
      return element ? new Date(element) : undefined;
    }

  //Listens to any change in the row quantity input and adjust the model
  rowChange(event) {
    if (event === null){
      event = this.modelList.length;
    }

    const modelListLength = this.modelList.length;
    const diff = Math.abs(modelListLength - event);

    if(modelListLength>event){
      this.modelList.splice(modelListLength-diff)
    } else {
      for(let i = 0; i < diff; i++){
        //Creates new obj to create new rows
        let obj = null;
        obj = this.createRow();
        this.modelList.push(obj);
      }
    }

    this.multipleRows = this.modelList.length > 1 ? true : false;
    this.updateModel();
  }

  //Adds new item to the prop list to create new column with the three dot menu
  rowColumns(list: IColumnDetails[]){
    let newList: string[] = list.map(x => x.columnName);
    newList.push('documentMenu');
    return newList;
  }

  onSave = async (file: FileUpload) => {
    if(file.applyToAll){
      this.modelList.forEach(elem => {
        elem.photo = file.file;
      });
    } else {
      this.imageElement.photo = file.file;
    }

   }

   displayModal = (event: Event, element: any): void => {
    this.imageElement = element;

    this.imageLoaderModal(event);
  }

  updateModel() : void {
    this.dataSource.data = [...this.modelList];
    this.upperList[0].args = [{title: this.title, inputProperties: this.inputProperties, data: this.modelList}];
    this.upperList[1].args = this.modelList;
    this.table.renderRows();
  }

  focusOutFunction(event) {
    const element = event.target as HTMLInputElement;
    this.errorCounter = this.errorCounter.filter(x => x != element);

    if (!element.value) {
      element.style.borderColor = 'red';
      this.errorCounter.push(element);
    } else {
      element.style.borderColor = 'rgba(164, 164, 164, 0.4)';
    }
    this.missingInfo = this.errorCounter.length > 0;
  }

  //Creates a new object to insert in the list using the base model
  createRow(data?: ITableData): ITableData {
    const newData = Object.assign({}, this.baseModel);

    if (data !== undefined) {
      const props = Object.keys(newData);
      props.forEach(prop => {
        const property = this.inputProperties.find(x => x.columnName === prop); 

        if (property.columnType === 'dropDown') {
          let structure: IOption = property.options.find(employee => employee.value === data[prop]);
          if (structure) {
            structure.selected = true;
            structure.disabled = false;
            newData[prop] = structure;

            property.options.find(employee => employee.value === data[prop]).selected = true;
          }
        }
        else if (property.columnType === 'boolean') {
          newData[prop] = data[prop] === 'Y' ? true : false;
        }
        else if (prop !== 'file')
          newData[prop] = data[prop];
      });
    }

    return newData;
  }

  handleCSVData(event: IFileData) {
    const errors = this.validateCSV({ event: event, title: this.title });
    if (errors.length > 0) {
      this.csvErrors.emit(errors);
      if (errors.filter(x => x.type === 'error').length > 0) {
        return;
      }
    }
    const rows = CSVService.transformData(event.data);
    this.createRows(rows);
  }

  createRows(rows: ITableData[]){
    this.modelList = [];

    rows.forEach((elem: ITableData) => {
      //Creates new obj to create new rows
      let obj: ITableData = this.createRow(elem);

      this.modelList.push(obj);
    });

    this.updateModel();
    this.tableRows = rows.length;
  }

  //Filter for decimals
  keyPressDecimal(event : KeyboardEvent) {

    let value = (event.target as HTMLInputElement).value;

    let key = event.key;

    if (value.includes(".") || value == ""){
      return key in "0123456789".split('');

    } else {
      return key in "0123456789".split('') || key == ".";

    }
  }

  //Filter for integers
  keyPressInt(event : KeyboardEvent) {
    let key = event.key;
      return key in "0123456789".split('');
  }

  //Get input style
  getInputStyle(inputSize: string){
    switch (inputSize) {
      case 'small':
        return 'table-input-text-small';
      case 'medium':
        return 'table-input-text-medium';
      case 'large':
        return 'table-input-text-large';
      default:
        return 'table-input-text-medium';
    }
  }  

  //Get columns that are not cloneable
  getNonCloneableColumns(){
    let columns: string[] = [];
    this.inputProperties.forEach((property: IColumnDetails) => {
      if(!property.cloneable){
        columns.push(property.columnHeader);
      }
    });

    return columns.join(', ');
  }

  onScroll() {
    this.scrollService.scrollDetected();
  }

  //Callback used to import and export data as CSV
  exportCSV(args: ITableExportData[]) {
    const argsData = args[0];
    const title = argsData.title;
    const inputProperties = argsData.inputProperties;
    const data = argsData.data;

    //Gets hidden element from DOM
    let elem = document.getElementById("csvLink") as HTMLAnchorElement;
    let csvData: { [header: string]: string | number | boolean }[] = [];

    //Goes trough data creating data element for CSV export
    data.forEach((elem: any) => {
      let row: { [header: string]: string | number | boolean } = {};
      let props = Object.keys(elem);

      props.forEach((prop) => {
        if (inputProperties.find((property: IColumnDetails) => property.columnName === prop)?.columnType === 'dropDown') {
          row[prop] = elem[prop].value;
        } else if (inputProperties.find((property: IColumnDetails) => property.columnName === prop)?.exportToCSV) {
          if (typeof elem[prop] === 'string') {
            row[prop] = elem[prop];
          } else if (typeof elem[prop] === 'boolean') {
            row[prop] = elem[prop] ? 'Y' : 'N';
          } else {
            row[prop] = elem[prop].text;
          }
        }
      });
      csvData.push(row);
    });

    //Sets download link in element and execute download
    let csvTitle = "";
    if (title) {
      csvTitle = `csv_template_${StringHelpers.convertToFileFormat(title)}`;
    } else {
      csvTitle = "csv_template";
    }
    CSVService.exportCSV(elem, csvTitle, csvData);
    elem.click();
  };

  importCSV() {

  }

}
